import type { SignalDetail } from '../../../types/signals-live';
import { Badge } from '../../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Timestamp } from '../../ui/timestamp';
import { toHumanCase } from './signalsLiveDetailUtils';

interface SignalCustomerSidebarProps {
  signal: SignalDetail;
}

export function SignalCustomerSidebar({ signal }: SignalCustomerSidebarProps) {
  return (
    <div className="w-80 space-y-6 overflow-y-auto scrollbar-hide">
      <Card>
        <CardHeader>
          <div className="flex items-center">
            <div className="mr-3 flex h-12 w-12 items-center justify-center rounded-full bg-gray-200">
              <span className="text-lg font-medium text-gray-700">
                {(signal.customer?.name || signal.customer_name)
                  ?.split(' ')
                  .map((name: string) => name[0])
                  .join('')
                  .toUpperCase() || 'UK'}
              </span>
            </div>
            <div>
              <CardTitle className="text-base">
                {signal.customer?.name || signal.customer_name || 'Unknown Customer'}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Case #{signal.customer?.id || signal.customer_id || signal.source_id || signal.id?.slice(0, 8).toUpperCase()}
              </p>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3 text-sm">
            <div>
              <p className="mb-1 text-xs text-muted-foreground">Email</p>
              <p className="text-foreground">{signal.customer?.email || signal.customer_email || 'Not provided'}</p>
            </div>
            <div>
              <p className="mb-1 text-xs text-muted-foreground">Phone</p>
              <p className="text-foreground">{signal.customer?.phone || signal.customer_phone || 'Not provided'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Active Products / Policies</CardTitle>
        </CardHeader>
        <CardContent>
          {signal.customer?.product_policy || signal.customer_product_policy ? (
            <div className="rounded-lg bg-muted p-3">
              <p className="text-sm font-medium">
                {signal.customer?.product_policy || signal.customer_product_policy}
              </p>
            </div>
          ) : (
            <div className="rounded-lg bg-muted p-3">
              <p className="text-sm italic text-muted-foreground">No active products</p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Vulnerability Profile</CardTitle>
        </CardHeader>
        <CardContent>
          {signal.classification?.vulnerability_labels?.length ? (
            <>
              <p className="mb-2 text-xs text-muted-foreground">
                {signal.classification.vulnerability_labels.length} flags / 5 in:
              </p>
              <div className="flex flex-wrap gap-2">
                {signal.classification.vulnerability_labels.map((label: string, index: number) => (
                  <Badge key={index} variant="secondary">
                    {toHumanCase(label)}
                  </Badge>
                ))}
              </div>
            </>
          ) : (
            <p className="text-xs italic text-muted-foreground">
              {signal.classification ? 'No vulnerability flags detected' : 'Awaiting classification...'}
            </p>
          )}
        </CardContent>
      </Card>

      <div className="mb-6">
        <h4 className="mb-3 text-xs text-gray-500">Active Vulnerabilities</h4>
        <div className="space-y-2">
          {signal.classification?.vulnerability_detected ? (
            signal.classification.vulnerability_labels?.map((label: string, index: number) => (
              <div key={index} className="flex items-center justify-between text-sm">
                <span className="capitalize text-gray-700">{label.replace(/_/g, ' ').toLowerCase()}</span>
                <span className="rounded-full bg-yellow-100 px-2 py-0.5 text-xs text-yellow-700">Active</span>
              </div>
            ))
          ) : (
            <p className="text-sm text-gray-500">None detected</p>
          )}
        </div>
      </div>

      <div>
        <h4 className="mb-3 text-xs text-gray-500">Signals History</h4>
        <div className="mb-2 text-xs text-gray-600">
          <span className="font-medium">Last 60 Days</span>
          <span className="float-right font-medium">3 Signals</span>
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-gray-200 text-gray-500">
              <th className="py-2 text-left font-normal">Date</th>
              <th className="py-2 text-center font-normal">Type</th>
              <th className="py-2 text-right font-normal">Summary</th>
            </tr>
          </thead>
          <tbody className="text-gray-700">
            {signal.history?.length ? (
              signal.history.slice(0, 3).map((item, index) => (
                <tr key={index} className="border-b border-gray-100">
                  <td className="py-2">
                    <Timestamp value={item.created_at} format="date-only" />
                  </td>
                  <td className="py-2 text-center capitalize">{item.action}</td>
                  <td className="py-2 text-right">{item.summary || 'N/A'}</td>
                </tr>
              ))
            ) : (
              <tr className="border-b border-gray-100">
                <td colSpan={3} className="py-4 text-center text-xs italic text-gray-500">
                  No signal history available
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {!!signal.history?.length && (
          <div className="mt-3">
            <p className="text-xs text-gray-600">
              <span className="font-medium">Themes:</span>
              <br />
              <span className="text-gray-700">Based on customer history</span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
